import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { admin, ApiError, errorMessage } from "../lib/api";
import { fromMinor, toMinor } from "../lib/format";
import type { Category, List, Product, ProductImage, Variant } from "../lib/types";
import SeoFields, { plain, type Seo } from "../components/SeoFields";
import { Alert, Button, Card, Field, PageHeader, Spinner, Toggle } from "../components/ui";
import { ImageManager } from "../components/ImageManager";

// Rând de variantă editabil (prețurile ca text în lei, opțiunile ca „Mărime: M; Culoare: Roșu”).
type Row = {
  id?: string;
  sku: string;
  options: string;
  price: string;
  compare: string;
  available: string;
  track: boolean;
  dirty?: boolean;
  original?: Variant;
};

const optionsToText = (o: Record<string, string>) => Object.entries(o).map(([k, v]) => `${k}: ${v}`).join("; ");
function textToOptions(s: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of s.split(";")) {
    const [k, ...v] = part.split(":");
    if (k.trim() && v.join(":").trim()) out[k.trim()] = v.join(":").trim();
  }
  return out;
}
const toRow = (v: Variant): Row => ({
  id: v.id, sku: v.sku, options: optionsToText(v.options), price: fromMinor(v.price), compare: fromMinor(v.compare_at_price),
  available: String(v.available), track: v.track_inventory, original: v,
});
const emptyRow = (): Row => ({ sku: "", options: "", price: "", compare: "", available: "0", track: true, dirty: true });

function rowPayload(r: Row) {
  const price = toMinor(r.price);
  if (price === null) throw new Error(`Preț invalid pentru varianta ${r.sku || r.options || "nouă"}`);
  return {
    sku: r.sku.trim(), options: textToOptions(r.options), price,
    compare_at_price: r.compare.trim() ? toMinor(r.compare) : null, track_inventory: r.track,
  };
}

export default function ProductEdit() {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();
  const qc = useQueryClient();
  const product = useQuery({ queryKey: ["product", id], queryFn: () => admin.get<Product>(`/products/${id}`), enabled: !isNew });
  const categories = useQuery({ queryKey: ["categories"], queryFn: () => admin.get<List<Category>>("/categories") });

  const [f, setF] = useState({ title: "", handle: "", description: "", status: "draft", vendor: "", tags: "", category_ids: [] as string[], images: [] as ProductImage[], seo: {} as Seo });
  const [rows, setRows] = useState<Row[]>([emptyRow()]);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const p = product.data;
    if (!p) return;
    setF({
      title: p.title, handle: p.handle, description: p.description, status: p.status, vendor: p.vendor,
      tags: p.tags.join(", "), category_ids: p.category_ids, images: p.images, seo: p.seo ?? {},
    });
    setRows(p.variants.map(toRow));
  }, [product.data]);

  const body = () => ({
    title: f.title, description: f.description, status: f.status, vendor: f.vendor,
    tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
    category_ids: f.category_ids,
    images: f.images.map((i) => ({ url: i.url, alt: i.alt || f.title })),
    seo: { title: f.seo.title?.trim() || undefined, description: f.seo.description?.trim() || undefined },
    ...(f.handle ? { handle: f.handle } : {}),
  });

  const done = (p: Product) => {
    qc.invalidateQueries({ queryKey: ["products"] });
    qc.setQueryData(["product", p.id], p);
    setError(undefined);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  const onError = (e: unknown) => {
    setError(e instanceof ApiError && e.problem.status === 412 ? "Produsul a fost modificat între timp de altcineva. Reîncarcă pagina." : errorMessage(e));
  };

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) {
        const variants = rows.map((r) => ({ ...rowPayload(r), available: Number(r.available) || 0 }));
        return admin.post<Product>("/products", { ...body(), variants });
      }
      return admin.patch<Product>(`/products/${id}`, body(), { "If-Match": `"${product.data!.version}"` });
    },
    onSuccess: (p) => {
      done(p);
      if (isNew) nav(`/products/${p.id}`, { replace: true });
    },
    onError,
  });

  const saveRow = useMutation({
    mutationFn: async (i: number) => {
      const r = rows[i];
      let p: Product;
      if (r.id) {
        p = await admin.patch<Product>(`/variants/${r.id}`, rowPayload(r));
        if (String(r.original?.available) !== r.available) {
          await admin.post("/inventory", { items: [{ variant_id: r.id, available: Number(r.available) || 0 }] });
          p = await admin.get<Product>(`/products/${id}`);
        }
      } else {
        p = await admin.post<Product>(`/products/${id}/variants`, { ...rowPayload(r), available: Number(r.available) || 0 });
      }
      return p;
    },
    onSuccess: (p) => { done(p); setRows(p.variants.map(toRow)); },
    onError,
  });

  const deleteRow = useMutation({
    mutationFn: (vid: string) => admin.del(`/variants/${vid}`).then(() => admin.get<Product>(`/products/${id}`)),
    onSuccess: (p) => { done(p); setRows(p.variants.map(toRow)); },
    onError,
  });

  const removeProduct = useMutation({
    mutationFn: () => admin.del(`/products/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["products"] }); nav("/products"); },
    onError,
  });

  if (!isNew && product.isLoading) return <Spinner />;
  const upd = (i: number, patch: Partial<Row>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch, dirty: true } : r)));
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });

  return (
    <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <PageHeader
        title={isNew ? "Produs nou" : f.title || "Produs"}
        subtitle={<Link to="/products" className="hover:text-brand">← Înapoi la produse</Link>}
        actions={<>
          {saved && <span className="self-center text-sm font-semibold text-emerald-600">✓ Salvat</span>}
          {!isNew && <Button type="button" variant="secondary" onClick={() => confirm("Ștergi definitiv produsul?") && removeProduct.mutate()}>Șterge</Button>}
          <Button disabled={save.isPending || uploading} title={uploading ? "Așteaptă terminarea încărcării imaginilor" : undefined}>
            {save.isPending ? "Se salvează…" : uploading ? "Se încarcă imaginile…" : "Salvează"}
          </Button>
        </>}
      />
      {error && <div className="mb-4"><Alert>{error}</Alert></div>}

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0 space-y-6">
          <Card title="Detalii">
            <div className="space-y-4">
              <Field label="Titlu"><input className="input" required value={f.title} onChange={set("title")} /></Field>
              <Field label="Descriere"><textarea className="input min-h-36" value={f.description} onChange={set("description")} /></Field>
              <div>
                <div className="mb-1 text-xs font-semibold text-slate-600">Imagini</div>
                <ImageManager images={f.images} onChange={(images) => setF((f) => ({ ...f, images }))} onBusy={setUploading} />
              </div>
            </div>
          </Card>

          <Card title="SEO (motoare de căutare)">
            <SeoFields value={f.seo} onChange={(seo) => setF((f) => ({ ...f, seo }))} fallbackTitle={f.title || "Titlul produsului"}
              fallbackDescription={plain(f.description) || "Începutul descrierii produsului."} path={`/p/${f.handle || "…"}`} />
          </Card>

          <Card title="Variante" actions={<Button type="button" size="sm" variant="secondary" onClick={() => setRows([...rows, emptyRow()])}>+ Variantă</Button>}>
            <div className="-mx-5 -my-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Opțiuni <span className="font-normal">(ex: Mărime: M)</span></th>
                    <th className="px-3 py-2">SKU</th><th className="px-3 py-2">Preț (lei)</th><th className="px-3 py-2">Preț vechi</th>
                    <th className="px-3 py-2">Stoc</th><th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r, i) => (
                    <tr key={r.id ?? `new-${i}`} className="align-top">
                      <td className="px-3 py-2"><input className="input min-w-36" value={r.options} onChange={(e) => upd(i, { options: e.target.value })} placeholder="Default" /></td>
                      <td className="px-3 py-2"><input className="input w-28" value={r.sku} onChange={(e) => upd(i, { sku: e.target.value })} /></td>
                      <td className="px-3 py-2"><input className="input w-24" inputMode="decimal" required value={r.price} onChange={(e) => upd(i, { price: e.target.value })} /></td>
                      <td className="px-3 py-2"><input className="input w-24" inputMode="decimal" value={r.compare} onChange={(e) => upd(i, { compare: e.target.value })} /></td>
                      <td className="px-3 py-2">
                        <input className="input w-20" inputMode="numeric" disabled={!r.track} value={r.available} onChange={(e) => upd(i, { available: e.target.value })} />
                        <label className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                          <input type="checkbox" checked={r.track} onChange={(e) => upd(i, { track: e.target.checked })} /> urmărește
                        </label>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        {!isNew && r.dirty && (
                          <Button type="button" size="sm" onClick={() => saveRow.mutate(i)} disabled={saveRow.isPending}>Salvează</Button>
                        )}
                        {(isNew ? rows.length > 1 : r.id && rows.length > 1) && (
                          <Button type="button" size="sm" variant="ghost" aria-label="Șterge varianta"
                            onClick={() => (isNew || !r.id ? setRows(rows.filter((_, j) => j !== i)) : deleteRow.mutate(r.id))}>✕</Button>
                        )}
                        {!isNew && !r.id && <Button type="button" size="sm" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}>Renunță</Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!isNew && <p className="mt-6 text-xs text-slate-400">Variantele se salvează individual, pe rând.</p>}
          </Card>
        </div>

        <div className="min-w-0 space-y-6">
          <Card title="Publicare">
            <div className="space-y-4">
              <Field label="Status">
                <select className="input" value={f.status} onChange={set("status")}>
                  <option value="draft">Ciornă (invizibil)</option>
                  <option value="active">Activ (vizibil în magazin)</option>
                  <option value="archived">Arhivat</option>
                </select>
              </Field>
              <Field label="URL (handle)" hint={isNew ? "Se generează automat din titlu" : undefined}>
                <input className="input font-mono text-xs" value={f.handle} onChange={set("handle")} placeholder="auto" />
              </Field>
            </div>
          </Card>
          <Card title="Organizare">
            <div className="space-y-4">
              <Field label="Brand / producător"><input className="input" value={f.vendor} onChange={set("vendor")} /></Field>
              <Field label="Etichete" hint="Separate prin virgulă"><input className="input" value={f.tags} onChange={set("tags")} /></Field>
              <div>
                <div className="mb-2 text-xs font-semibold text-slate-600">Categorii</div>
                <div className="max-h-56 space-y-1.5 overflow-y-auto">
                  {categories.data?.data.map((c) => (
                    <Toggle key={c.id} label={c.name} checked={f.category_ids.includes(c.id)}
                      onChange={(on) => setF({ ...f, category_ids: on ? [...f.category_ids, c.id] : f.category_ids.filter((x) => x !== c.id) })} />
                  ))}
                  {categories.data?.data.length === 0 && <p className="text-xs text-slate-400">Nu există categorii. <Link to="/categories" className="text-brand">Creează</Link></p>}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </form>
  );
}
